import { useMemo } from 'react';
import type { DecoratedEvent } from '@/features/events/types';
import {
  getIntegrationExportPlatform,
  hasAutoUpdateExport,
} from '@/features/interactions/lib/exportStatus.lib';
import {
  usePlatformExports,
  type EntryExport,
  type PlatformExport,
} from '@/features/magic/hooks/usePlatformExports';
import type { EventExportPlatform } from '@/types/gql.generated';
import {
  collectEventExportRows,
  getExportsForCalendar,
  hasStaleExport,
} from '../eventExportRows.lib';
import type { Destination } from '../types';

/**
 * The event has a server export row, scoped to one provider when known —
 * without the scope an event exported only elsewhere still warms the cache.
 */
const hasExportRows = (
  event: DecoratedEvent,
  platform?: EventExportPlatform
): boolean =>
  event.exports.some((row) => !platform || row.platform === platform);

type UseExportFormStatusParams = {
  destination: Destination | null;
  magicEvent: DecoratedEvent | null;
  events: DecoratedEvent[];
};

type UseExportFormStatusResult = {
  /** Whether the export was successful (for the selected destination) */
  isExported: boolean;
  /** Whether the export is stale and needs update */
  isStale: boolean;
  /** The local platform export record (needed for SuccessState) */
  platformExport: PlatformExport | null;
};

export const useExportFormStatus = ({
  destination,
  magicEvent,
  events,
}: UseExportFormStatusParams): UseExportFormStatusResult => {
  const { platformExports } = usePlatformExports();
  const isBatch = !magicEvent;

  // The destination's provider, for scoping export-row reads. Undefined until
  // a destination is chosen (or should the destination ever not be a calendar
  // integration), in which case rows of any provider count.
  const exportPlatform = destination
    ? getIntegrationExportPlatform(destination.platform)
    : undefined;

  const isAutoUpdate =
    !isBatch &&
    !!exportPlatform &&
    hasAutoUpdateExport(magicEvent, exportPlatform);

  // Single mode considers just the one event; batch considers the whole set.
  const relevantEvents = useMemo(
    () => (magicEvent ? [magicEvent] : events),
    [magicEvent, events]
  );

  const serverRows = useMemo(
    () => collectEventExportRows(relevantEvents),
    [relevantEvents]
  );

  // Find local platform export for the selected destination
  const localPlatformExport = useMemo(
    () =>
      destination
        ? (platformExports.find(
            (item) => item.calendarId === destination.calendar.id
          ) ?? null)
        : null,
    [destination, platformExports]
  );

  // Merge local + server per-event into a single platform export
  const platformExport = useMemo((): PlatformExport | null => {
    if (!destination) return null;

    const serverExports = getExportsForCalendar(
      serverRows,
      destination.calendar.id
    );

    const entryExports: EntryExport[] = relevantEvents
      .map((event): EntryExport | null => {
        // 1. Prefer local Zustand entry (optimistic state from current session)
        const localEntry = localPlatformExport?.entryExports.find(
          (e) => e.entry.id === event.id
        );
        if (localEntry) return localEntry;

        // 2. Batch mode skips events with no export row for this provider.
        //    (Single mode always consults its event, so its rows decide.)
        if (isBatch && !hasExportRows(event, exportPlatform)) {
          return null;
        }

        // 3. Check server data for THIS specific calendar
        const serverExport = serverExports.find((e) => e.eventId === event.id);
        if (!serverExport) return null; // exported elsewhere, not to this calendar

        const isStale = hasStaleExport(
          serverRows,
          event.id,
          event.version,
          destination.calendar.id
        );

        return {
          entry: { id: event.id, startDate: event.startDate },
          externalLink: serverExport.externalLink ?? '',
          isStale,
          platform: destination.platform,
          destinationEntry: null,
        };
      })
      .filter((e): e is EntryExport => e !== null);

    if (entryExports.length === 0) return null;

    return {
      platform: destination.platform,
      integrationId: destination.integration.id,
      calendarId: destination.calendar.id,
      entryExports,
    };
  }, [
    destination,
    localPlatformExport,
    isBatch,
    exportPlatform,
    relevantEvents,
    serverRows,
  ]);

  // Derive exported status from the merged platform export
  const isExported = useMemo(() => {
    if (!platformExport) return false;

    const exportedIds = new Set(
      platformExport.entryExports.map((e) => e.entry.id)
    );

    return (
      relevantEvents.length > 0 &&
      relevantEvents.every((e) => exportedIds.has(e.id))
    );
  }, [platformExport, relevantEvents]);

  // Check if stale from merged entries + server fallback
  const isStale = useMemo(() => {
    if (!isExported || !platformExport || !destination) return false;
    if (isAutoUpdate) return false; // AutoUpdate events are never stale
    if (platformExport.entryExports.some((e) => e.isStale)) return true;
    // Server may know about staleness that local Zustand doesn't
    return relevantEvents.some((e) =>
      hasStaleExport(serverRows, e.id, e.version, destination.calendar.id)
    );
  }, [
    isExported,
    platformExport,
    destination,
    isAutoUpdate,
    relevantEvents,
    serverRows,
  ]);

  return {
    isExported,
    isStale,
    platformExport,
  };
};
